import { Link, useLocation } from "react-router-dom";
import { ChevronRight, Home } from "lucide-react";
import { ROUTE_LABELS } from "@/components/layout/navConfig";
import { cn } from "@/lib/utils";

/**
 * Auto-generates breadcrumb from the current URL using ROUTE_LABELS.
 * Also accepts explicit `crumbs` override:
 *   <PageBreadcrumb crumbs={[{ label: "Procurement" }, { label: "Purchase Orders" }]} />
 */
export function PageBreadcrumb({ crumbs, className }) {
  const location = useLocation();
  const inProject = !crumbs && /^\/projects\/[^/]+/.test(location.pathname);
  const items = inProject ? [] : crumbs || buildCrumbs(location.pathname);

  if (!items || items.length === 0) return null;

  return (
    <nav aria-label="breadcrumb" className={cn("flex flex-wrap items-center gap-1 text-xs text-muted-foreground", className)}>
      <Link to="/" className="flex items-center gap-1 transition-colors hover:text-foreground">
        <Home className="h-3 w-3" />
        <span className="hidden sm:inline">Home</span>
      </Link>
      {items.map((item, i) => (
        <span key={i} className="flex items-center gap-1">
          <ChevronRight className="h-3 w-3 opacity-50" />
          {item.to ? (
            <Link to={item.to} className="transition-colors hover:text-foreground">{item.label}</Link>
          ) : (
            <span className="font-medium text-foreground">{item.label}</span>
          )}
        </span>
      ))}
    </nav>
  );
}

function buildCrumbs(pathname) {
  // e.g. /erp/operations/vendors -> key = "operations/vendors"
  const erpMatch = pathname.match(/^\/erp\/([^/]+)\/([^/]+)/);
  if (erpMatch) {
    const key = `${erpMatch[1]}/${erpMatch[2]}`;
    const info = ROUTE_LABELS[key];
    if (info) {
      return [
        { label: info.groupLabel },
        { label: info.label },
      ];
    }
  }
  const adminMatch = pathname.match(/^\/erp\/governance\/(users|permission-matrix|roles-access|access|access-history|audit-log|org-settings|system-health|integrity-checks)$/);
  if (adminMatch) {
    const label = adminMatch[1].split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
    return [{ label: "Administration" }, { label }];
  }
  // /procurement/vendors style
  const parts = pathname.replace(/^\//, "").split("/").filter(Boolean);
  if (parts.length >= 2) {
    return parts.map((p, i) => ({
      label: p.split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" "),
      to: i < parts.length - 1 ? `/${parts.slice(0, i + 1).join("/")}` : undefined,
    }));
  }
  return [];
}
